import { styled } from "goober";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { playClick } from "../../lib/sounds";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { CodeContextLabel, MessageContent } from "./MessageContent";
import type { QueuedMessage } from "./queueStorage";

const QUEUE_VISIBLE_LIMIT = 4;

const QueueStackOverlay = styled("div")`
  position: absolute;
  bottom: calc(100% + 16.5px);
  left: 12px;
  right: 12px;
  pointer-events: none;
  isolation: isolate;
`;

const QueueCardBody = styled("div")`
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-primary);
  padding: 12px;
  border-radius: 12px;
  min-width: 0;
  width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: var(--elevation-low);
  transition:
    transform var(--motion-base) var(--ease-out),
    opacity var(--motion-base) var(--ease-out);
  transform-origin: bottom center;
  > .queue-card-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;

const QueueBadge = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  width: fit-content;
  flex-shrink: 0;
`;

const QueueInlineButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: var(--alpha4);
  color: var(--text-secondary);
  border-radius: 50%;
  &:hover {
    background: var(--alpha8);
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 1px;
  }
  &[data-focused="true"] {
    background: var(--alpha8);
    color: var(--text-primary);
  }
`;

// The card behind expands on click, which these buttons handled on pointerdown.
function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{ display: "flex", gap: 4 }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

const QueueEditTextarea = styled("textarea")`
  field-sizing: content;
  width: 100%;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  resize: none;
  outline: none;
  color: inherit;
  font-family: inherit;
  font-weight: 350;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -1%;
  white-space: pre-wrap;
  word-break: break-word;
`;

function QueueCardContent({
  card,
  actions,
  leftBadge,
  editor,
}: {
  card: QueuedMessage;
  actions?: React.ReactNode;
  leftBadge?: React.ReactNode;
  editor?: React.ReactNode;
}) {
  return (
    <>
      {(card.attachments ?? []).map((att) => (
        <CodeContextLabel key={att.id}>
          <Icon
            name={
              att.mediaType === "application/pdf" ? "file-scan" : "paperclip"
            }
            size={10}
          />
          <Text variant="text-small-md" ellipsis>
            {att.fileName}
          </Text>
        </CodeContextLabel>
      ))}
      {card.planMode && (
        <CodeContextLabel>
          <Icon name="list-todo" size={10} />
          <Text variant="text-small-md">Plan mode</Text>
        </CodeContextLabel>
      )}
      {editor ? (
        <>
          {editor}
          {(leftBadge || actions) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minHeight: 12,
              }}
            >
              {leftBadge}
              {actions && (
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {actions}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <MessageContent
          text={card.text}
          isDictated={!!card.isDictated}
          lineClamp={3}
          actions={actions}
          hideVoice
          leftBadge={leftBadge}
        />
      )}
    </>
  );
}

export function QueueStack({
  queue,
  overlayRef,
  onForceSend,
  onCancel,
  scrollContainer,
  selectedIndex,
  selectedButton,
  editingId,
  onEditStart,
  onEditCommit,
  onEditCancel,
}: {
  queue: QueuedMessage[];
  overlayRef: (el: HTMLDivElement | null) => void;
  onForceSend: (card: QueuedMessage) => void;
  onCancel: (id: string) => void;
  scrollContainer: React.RefObject<HTMLDivElement | null>;
  selectedIndex?: number | null;
  selectedButton?: "cancel" | "edit" | "send" | null;
  editingId?: string | null;
  onEditStart: (id: string) => void;
  onEditCommit: (id: string, text: string) => void;
  onEditCancel: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [draft, setDraft] = useState("");
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const editingIdRef = useRef<string | null>(null);
  const queueRef = useRef(queue);
  useEffect(() => {
    queueRef.current = queue;
  });
  const localRef = useRef<HTMLDivElement | null>(null);
  const heightsObserverRef = useRef<ResizeObserver | null>(null);

  const setOverlayRef = useCallback(
    (el: HTMLDivElement | null) => {
      localRef.current = el;
      overlayRef(el);
    },
    [overlayRef]
  );

  const cardElsRef = useRef<Set<HTMLDivElement>>(new Set());

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setHeights((prev) => {
        let next: Record<string, number> | null = null;
        for (const entry of entries) {
          const target = entry.target as HTMLElement;
          const id = target.dataset.cardId;
          if (!id) continue;
          const h =
            entry.borderBoxSize?.[0]?.blockSize ??
            target.getBoundingClientRect().height;
          if (prev[id] !== h) {
            if (!next) next = { ...prev };
            next[id] = h;
          }
        }
        return next ?? prev;
      });
    });
    heightsObserverRef.current = observer;
    for (const el of cardElsRef.current) observer.observe(el);
    return () => {
      observer.disconnect();
      heightsObserverRef.current = null;
    };
  }, []);

  const setCardEl = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    cardElsRef.current.add(el);
    heightsObserverRef.current?.observe(el);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (!localRef.current?.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const el = scrollContainer.current;
    if (!el) return;
    const handler = () => setExpanded(false);
    el.addEventListener("wheel", handler, { passive: true });
    el.addEventListener("touchmove", handler, { passive: true });
    return () => {
      el.removeEventListener("wheel", handler);
      el.removeEventListener("touchmove", handler);
    };
  }, [expanded, scrollContainer]);

  // Collapse once the queue no longer has anything to expand.
  if (queue.length <= 1 && expanded) setExpanded(false);

  const previousExpandedRef = useRef(expanded);
  useEffect(() => {
    if (previousExpandedRef.current === expanded) return;
    previousExpandedRef.current = expanded;
    const cardCount = Math.min(queue.length, QUEUE_VISIBLE_LIMIT);
    for (let i = 0; i < cardCount; i++) {
      setTimeout(() => playClick(), i * 30);
    }
  }, [expanded, queue.length]);

  useEffect(() => {
    editingIdRef.current = editingId ?? null;
    if (editingId == null) return;
    const card = queueRef.current.find((c) => c.id === editingId);
    if (!card) return;
    setDraft(card.text);
    requestAnimationFrame(() => {
      editTextareaRef.current?.focus();
      editTextareaRef.current?.select();
    });
  }, [editingId]);

  const commitEdit = () => {
    const id = editingIdRef.current;
    if (id == null) return;
    onEditCommit(id, draft);
  };

  const cancelEdit = () => {
    editingIdRef.current = null;
    onEditCancel();
  };

  if (queue.length === 0) return null;

  const PEEK = 6;
  const GAP = 8;
  const SCALE_PER_DEPTH = 0.03;
  const isExpanded = expanded || selectedIndex != null || editingId != null;
  const cards = isExpanded ? queue : queue.slice(-QUEUE_VISIBLE_LIMIT);
  const canExpand = queue.length > 1;
  const frontCard = cards[cards.length - 1];
  const frontHeight = frontCard ? (heights[frontCard.id] ?? 80) : 80;

  type Layout = {
    card: QueuedMessage;
    isFront: boolean;
    top: number;
    transform: string;
    queueIndex: number;
  };
  const layouts: Layout[] = [];
  let cumulativeTop = 0;
  const offset = queue.length - cards.length;
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (!card) continue;
    const isFront = i === cards.length - 1;
    const depthFromFront = cards.length - 1 - i;
    const cardHeight = heights[card.id] ?? 80;
    const queueIndex = offset + i;
    if (isExpanded) {
      layouts.push({
        card,
        isFront,
        queueIndex,
        top: cumulativeTop,
        transform: "scale(1)",
      });
      cumulativeTop += cardHeight + GAP;
    } else {
      layouts.push({
        card,
        isFront,
        queueIndex,
        top: i * PEEK,
        transform: `scale(${1 - depthFromFront * SCALE_PER_DEPTH})`,
      });
    }
  }

  let containerHeight = 0;
  if (isExpanded) {
    containerHeight = cumulativeTop > 0 ? cumulativeTop - GAP : 0;
  } else {
    containerHeight = (cards.length - 1) * PEEK + frontHeight;
  }

  const renderActions = (
    card: QueuedMessage,
    includeForceSend: boolean,
    focusedButton: "cancel" | "edit" | "send" | null
  ) => (
    <ActionRow>
      <Tooltip content="Cancel">
        <QueueInlineButton
          type="button"
          data-focused={focusedButton === "cancel"}
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => onCancel(card.id)}
        >
          <Icon name="x" size={12} />
        </QueueInlineButton>
      </Tooltip>
      <Tooltip content="Edit">
        <QueueInlineButton
          type="button"
          data-focused={focusedButton === "edit"}
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => onEditStart(card.id)}
        >
          <Icon name="pencil" size={12} />
        </QueueInlineButton>
      </Tooltip>
      {includeForceSend && (
        <Tooltip content="Send now">
          <QueueInlineButton
            type="button"
            data-focused={focusedButton === "send"}
            onMouseDown={(e) => e.preventDefault()}
            onPress={() => onForceSend(card)}
          >
            <Icon
              name={focusedButton === "send" ? "corner-down-left" : "arrow-up"}
              size={12}
            />
          </QueueInlineButton>
        </Tooltip>
      )}
    </ActionRow>
  );

  const handleStackClick = canExpand ? () => setExpanded(!expanded) : undefined;

  return (
    <QueueStackOverlay ref={setOverlayRef}>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: containerHeight,
          transition: "height var(--motion-base) var(--ease-out)",
          pointerEvents: "none",
        }}
      >
        {layouts.map(({ card, isFront, top, transform, queueIndex }) => {
          const isSelected = queueIndex === selectedIndex;
          const isEditingThisCard = card.id === editingId;
          return (
            <QueueCardBody
              key={card.id}
              ref={setCardEl}
              data-card-id={card.id}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top,
                transform,
                transformOrigin: "top center",
                maxHeight: !isExpanded && !isFront ? frontHeight : undefined,
                overflow: !isExpanded && !isFront ? "hidden" : undefined,
                transition:
                  "top var(--motion-base) var(--ease-out), transform var(--motion-base) var(--ease-out)",
                cursor: canExpand ? "pointer" : "default",
                pointerEvents: "auto",
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleStackClick}
            >
              <div className="queue-card-content">
                <QueueCardContent
                  card={card}
                  editor={
                    isEditingThisCard ? (
                      <QueueEditTextarea
                        ref={editTextareaRef}
                        value={draft}
                        rows={1}
                        onChange={(e) => setDraft(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            commitEdit();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            e.stopPropagation();
                            cancelEdit();
                          }
                        }}
                        onBlur={commitEdit}
                      />
                    ) : undefined
                  }
                  actions={
                    isEditingThisCard ? (
                      <ActionRow>
                        <Tooltip content="Done">
                          <QueueInlineButton
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onPress={commitEdit}
                          >
                            <Icon name="check" size={12} />
                          </QueueInlineButton>
                        </Tooltip>
                      </ActionRow>
                    ) : isExpanded || isFront ? (
                      renderActions(
                        card,
                        true,
                        isSelected ? (selectedButton ?? null) : null
                      )
                    ) : undefined
                  }
                  leftBadge={
                    isExpanded || isFront ? (
                      <QueueBadge>
                        <Icon
                          name={
                            card.state === "failed" ? "circle-off" : "clock"
                          }
                          size={10}
                        />
                        <Text variant="text-small-md">
                          {card.state === "failed"
                            ? "Failed to send"
                            : "Queued"}
                        </Text>
                      </QueueBadge>
                    ) : undefined
                  }
                />
              </div>
            </QueueCardBody>
          );
        })}
      </div>
    </QueueStackOverlay>
  );
}
